import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';

interface User {
    id: string;
    name: string;
    email: string;
    company: string;
    designation?: string;
    description?: string;
    status: string;
    assigned_user?: string;
    scheduled_date?: string;
    scheduled_time?: string;
    lead_label?: string;
    interaction_history?: InteractionNote[];
    created_at: string;
    updated_at: string;
}

interface InteractionNote {
    meta: string;
    text: string;
    timestamp: Date;
}

interface SavePayload {
    status: string;
    assigned_user: string;
    scheduled_date: string | null;
    scheduled_time: string | null;
    lead_label: string | null;
    interaction_history: InteractionNote[];
}

interface DemoInteractionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (payload: SavePayload) => void;
    feedbackId: string;
}

type HeatLevel = '' | 'Hot' | 'Warm' | 'Cold';

const DemoInteractionModal: React.FC<DemoInteractionModalProps> = ({
    isOpen = true,
    onClose,
    onSave,
    feedbackId,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [assignee, setAssignee] = useState('Rajdeep');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [heat, setHeat] = useState<HeatLevel>('');
    const [noteText, setNoteText] = useState('');
    const [notes, setNotes] = useState<InteractionNote[]>([]);
    const [status, setStatus] = useState('');
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const overlayRef = useRef<HTMLDivElement>(null);
    const apiURL = process.env.REACT_APP_API_URL
    const { hasAuthenticated, userDetails } = useAuth();

    // Fetch user data from API
    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${apiURL}/demo-request/${feedbackId}`, {
                    method: 'GET',
                    headers: {
                        'accept': 'application/json',
                        'Token': hasAuthenticated || '',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
                }

                const userData: User = await response.json();
                setUser(userData);

                // Set assignee from API data if available
                if (userData.assigned_user) {
                    setAssignee(userData.assigned_user);
                }
                if (userData.status) {
                    setStatus(userData.status);
                }
                if (userData.scheduled_date) {
                    setDate(userData.scheduled_date);
                }
                if (userData.scheduled_time) {
                    const formattedTime = userData.scheduled_time.slice(0, 5); // "11:12"
                    setTime(formattedTime);
                }
                if (userData.lead_label) {
                    setHeat(userData.lead_label as HeatLevel);
                }
                if (userData.interaction_history) {
                    const sortedNotes = userData.interaction_history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                    setNotes(sortedNotes);
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch user data');
                console.error('Error fetching user data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchUserData();
        }
    }, [feedbackId, isOpen, apiURL, hasAuthenticated]);

    const getInitials = (name: string): string => {
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getHeatBadgeClasses = (heatLevel: HeatLevel): string => {
        switch (heatLevel) {
            case 'Hot': return 'bg-red-500 text-white';
            case 'Warm': return 'bg-amber-500 text-black';
            case 'Cold': return 'bg-sky-400 text-white';
            default: return 'bg-gray-200 text-gray-800';
        }
    };

    const getStatusBadgeClasses = (status: string): string => {
        switch (status.toLowerCase()) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'in-progress': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const addNote = () => {
        const text = noteText.trim();
        if (!text) return;

        const newNote: InteractionNote = {
            meta: `${userDetails?.username} • ${new Date().toLocaleString()}`,
            text: text,
            timestamp: new Date()
        };

        setNotes(prev => [newNote, ...prev]);
        setNoteText('');
    };

    const clearNote = () => {
        setNoteText('');
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        const payload: SavePayload = {
            status: status,
            assigned_user: assignee,
            scheduled_date: date || null,
            scheduled_time: time || null,
            lead_label: heat || null,
            interaction_history: notes
        };

        try {
            const res = await fetch(`${apiURL}/demo-request/${feedbackId}/status`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Token': hasAuthenticated || '',
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                throw new Error("Failed to update status");
            }
            await res.json();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Failed to fetch user data');
            console.error("Update error:", error);
        }

        console.log('SAVE_PAYLOAD', payload);
        if (onSave) {
            onSave(payload);
        } else {
            alert('Saved (mock). Check console for payload.');
        }
        setLoading(false);
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current) {
            handleClose();
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                handleSave();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, assignee, date, time, heat, notes]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50 p-4"
            ref={overlayRef}
            onClick={handleOverlayClick}
        >
            <div
                className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-screen grid grid-rows-[auto_1fr_auto]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="title"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold tracking-wide text-gray-900" id="title">
                        Demo Interaction
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Admin-only popup to triage an incoming demo request.
                    </p>
                </div>

                {/* Body */}
                <div className="p-5 overflow-auto space-y-4">
                    {/* Loading State */}
                    {loading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-3 text-gray-600">Loading user data...</span>
                        </div>
                    )}

                    {/* Error State */}
                    {error && !user && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <div className="flex items-center">
                                <div className="text-red-600 font-medium">Error loading user data</div>
                            </div>
                            <div className="text-red-600 text-sm mt-1">{error}</div>
                            {feedbackId && (
                                <div className="text-gray-600 text-xs mt-2">User ID: {feedbackId}</div>
                            )}
                        </div>
                    )}

                    {/* User Data Display */}
                    {user && (
                        <>
                            {/* Persona bar (compact) */}
                            <div className="flex items-center gap-3 p-3 bg-blue-50 border border-gray-200 rounded-xl">
                                <div className="h-8 w-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                                    {getInitials(user.name)}
                                </div>
                                <div className="flex flex-wrap gap-2 text-sm flex-1">
                                    <span className="bg-gray-100 px-2 py-1 rounded-full" title="Name">
                                        {user.name}
                                    </span>
                                    <span className="bg-gray-100 px-2 py-1 rounded-full" title="Email">
                                        {user.email}
                                    </span>
                                    <span className="bg-gray-100 px-2 py-1 rounded-full" title="Company">
                                        {user.company}
                                    </span>
                                    <span className="bg-gray-100 px-2 py-1 rounded-full" title="Designation">
                                        {user.designation}
                                    </span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClasses(user.status)}`} title="Status">
                                        {user.status}
                                    </span>
                                </div>
                                <button
                                    className="text-xs text-gray-600 underline hover:text-gray-800 ml-auto"
                                    type="button"
                                    onClick={() => setShowDetails(!showDetails)}
                                >
                                    {showDetails ? 'Hide details' : 'View full details'}
                                </button>
                            </div>

                            {/* Collapsible full user block */}
                            {showDetails && (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {/* <div>
                                            <label className="block text-xs text-gray-500 mb-1">ID</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed font-mono"
                                                value={user.id}
                                                readOnly
                                            />
                                        </div> */}
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Name</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={user.name}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Email</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={user.email}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Company</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={user.company}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Designation</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={user.designation}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Created At</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={new Date(user.created_at).toLocaleString()}
                                                readOnly
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Updated At</label>
                                            <input
                                                className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm cursor-not-allowed"
                                                value={new Date(user.updated_at).toLocaleString()}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Description</label>
                                        <textarea
                                            className="w-full border border-dashed border-gray-300 bg-gray-50 text-gray-600 rounded-xl px-3 py-2 text-sm min-h-20 resize-vertical cursor-not-allowed"
                                            value={user.description}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Primary controls */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1" htmlFor="assignee">
                                        Handled by
                                    </label>
                                    <select
                                        id="assignee"
                                        className="w-full border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={assignee}
                                        onChange={(e) => setAssignee(e.target.value)}
                                    >
                                        <option value="Rajdeep">Rajdeep</option>
                                        <option value="Nayan">Nayan</option>
                                        <option value="Deepak">Deepak</option>
                                        <option value="Puru">Puru</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1" htmlFor="date">
                                        Date
                                    </label>
                                    <input
                                        id="date"
                                        type="date"
                                        className="w-full border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1" htmlFor="time">
                                        Time
                                    </label>
                                    <input
                                        id="time"
                                        type="time"
                                        className="w-full border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={time}
                                        onChange={(e) => setTime(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Lead label */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1" htmlFor="heat">
                                        Lead Label
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <select
                                            id="heat"
                                            className="flex-1 max-w-60 border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={heat}
                                            onChange={(e) => setHeat(e.target.value as HeatLevel)}
                                        >
                                            <option value="">Hot / Warm / Cold</option>
                                            <option value="Hot">Hot</option>
                                            <option value="Warm">Warm</option>
                                            <option value="Cold">Cold</option>
                                        </select>
                                        {heat && (
                                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getHeatBadgeClasses(heat)}`}>
                                                {heat}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1" htmlFor="heat">
                                        Status
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="status"
                                            type='text'
                                            className="w-full border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Interaction history */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="font-semibold text-gray-900">Interaction History</span>
                                    <span className="text-xs text-gray-500">Visible to sales team</span>
                                </div>
                                <textarea
                                    className="w-full border border-gray-200 bg-white text-gray-900 rounded-xl px-3 py-2 text-sm min-h-20 resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Add a note from the call / chat…"
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                />
                                <div className="flex gap-2 mt-2">
                                    <button
                                        className="border border-gray-200 bg-white text-gray-900 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        onClick={addNote}
                                    >
                                        Add Note
                                    </button>
                                    <button
                                        className="border border-gray-200 bg-white text-gray-900 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="button"
                                        onClick={clearNote}
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="mt-3 max-h-40 overflow-auto space-y-2">
                                    {notes.map((note, index) => (
                                        <div key={index} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                                            <div className="text-xs text-gray-600">
                                                <strong>{note.meta.split(' • ')[0]}</strong> • {note.meta.split(' • ')[1]}
                                            </div>
                                            <div className="text-sm text-gray-900 mt-1">{note.text}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Sticky Footer */}
                <div className="sticky bottom-0 bg-gradient-to-t from-white to-white/80 border-t border-gray-200 px-5 py-3 flex justify-end gap-2">
                    <button
                        className="border border-gray-200 bg-transparent text-gray-900 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onClick={handleClose}
                    >
                        Close
                    </button>
                    <button
                        className="bg-gray-900 text-white border-gray-900 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSave}
                        disabled={loading || (!user)}
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DemoInteractionModal;